import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { buildContractContext } from '../utils/contractContext';
import { printContract } from '../utils/renderContract';
import { invoiceDisplayNumber } from '../utils/invoiceDoc';
import { printInvoice } from './InvoiceViewModal';

const PAY_LABELS = {
  stripe: '💳 Card (Stripe)',
  viva:   '🔵 Viva Wallet',
  paypal: '🅿️ PayPal',
  cash:   '💵 Cash on pickup',
  bank:   '🏦 Bank Transfer',
  online: '🌐 Pre-paid online',
};

export default function CalDetailModal() {
  const {
    calDetail, setCalDetail,
    bookings, rentals, vehicles, users, roles, invoices,
    openModal, showToast,
    setRentals, setRentalHistory, setRentalDraft,
    assignRental, unassignRental, getUserName, currentUserId, canEdit,
    companySettings, resolveAgreementTemplate,
  } = useApp();

  function openContract(r, autoPrint) {
    const v = vehicles.find(vv => vv.id === r.vid);
    const tmpl = r.agreementSaved?.body
      ? { name: r.agreementSaved.templateName, body: r.agreementSaved.body, clauses: r.agreementSaved.clauses }
      : (resolveAgreementTemplate ? resolveAgreementTemplate(r.vid, r.type === 'lease' ? 'lease' : 'rental') : null);
    const ctx = r.agreementSaved?.context || buildContractContext({
      rental: r, vehicle: v, company: companySettings,
      signatureDataUrl: r.signData || r.presignData,
      signedAt: r.signedAt || r.presignedAt,
    });
    printContract(tmpl, ctx, { title: `Contract ${r.id || ''}`, autoPrint });
  }

  const [editing, setEditing] = useState(false);
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [editT1, setEditT1] = useState('');
  const [editT2, setEditT2] = useState('');

  const { open, bid } = calDetail;
  if (!open || !bid) return null;

  // Look up rental directly — calendar blocks now use rental IDs
  const rental = rentals.find(r => r.id === bid);
  if (!rental) return null;
  const v        = vehicles.find(x => x.id === rental.vid);
  // Real payments breakdown from linked pro formas (online + desk split)
  const linkedFin  = (invoices || []).filter(i => i.contract === rental.id || i.contractId === rental.id);
  const paidFin    = linkedFin.filter(i => i.status === 'paid');
  const paidTotFin = paidFin.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
  const dueFin     = Math.max(0, (parseFloat(rental.agreedPrice) || 0) - paidTotFin);
  // Cash actually collected = sum of PAID cash pro formas (self-corrects stale rental.cashAmount)
  const cashPaidFin = paidFin.filter(i => i.method === 'cash').reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
  const isOnline = rental.source === 'online';
  const now      = new Date();
  const isOverdue = (rental.status === 'active' || rental.status === 'overdue') && new Date(rental.end) < now;
  const bType    = isOverdue ? 'overdue' : rental.status === 'active' ? 'rental' : 'booking';
  // Compatibility shim so existing JSX using b.* still works
  const days     = rental.start && rental.end
    ? Math.max(1, Math.round((new Date(rental.end) - new Date(rental.start)) / 86400000))
    : rental.calSpan || 1;
  const b = { id: rental.id, vid: rental.vid, label: rental.customer, type: bType, source: rental.source, span: days };

  function close() { setEditing(false); setCalDetail({ open: false, bid: null }); }

  function beginEdit() {
    setEditStart(rental.start ? rental.start.slice(0, 10) : '');
    setEditEnd(rental.end ? rental.end.slice(0, 10) : '');
    setEditT1(rental.t1 || '');
    setEditT2(rental.t2 || '');
    setEditing(true);
  }

  function saveEdit() {
    const fmtLabel = (iso) => {
      if (!iso) return '';
      return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };
    setRentals(prev => prev.map(r => {
      if (r.id !== rental.id) return r;
      const newStart = editStart ? editStart + 'T' + (editT1 || '09:00') : r.start;
      const newEnd   = editEnd   ? editEnd   + 'T' + (editT2 || '09:00') : r.end;
      const days = Math.max(1, Math.round((new Date(newEnd) - new Date(newStart)) / 86400000));
      return {
        ...r,
        start: newStart, end: newEnd,
        t1: editT1, t2: editT2,
        startLabel: fmtLabel(newStart), endLabel: fmtLabel(newEnd),
        calSpan: days,
      };
    }));
    setEditing(false);
    showToast('✅ Booking dates updated');
  }

  function startRental() {
    if (rental) { setRentalDraft(rental); openModal('check-in'); }
    else { openModal('add-rental', b.vid); }
    close();
  }

  function cancelBooking() {
    const name = rental?.customer || b.label;
    if (!window.confirm(`Cancel booking for ${name}? This cannot be undone.`)) return;
    if (rental) {
      setRentals(prev => prev.filter(r => r.id !== rental.id));
      setRentalHistory(prev => [{
        ...rental,
        status: 'cancelled',
        total: rental.agreedPrice,
        condRet: null,
        condRetNote: 'Booking cancelled.',
      }, ...prev]);
    }
    // bookings array is no longer the source of truth — rental already removed above
    showToast('Booking cancelled');
    close();
  }

  // ── Helper renderers ──
  function Row({ label, value, mono, style }) {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: 12, padding: '5px 0', borderBottom: '1px solid var(--bd)' }}>
        <span style={{ color: 'var(--tx3)', flexShrink: 0, marginRight: 10 }}>{label}</span>
        <span style={{ fontWeight: 500, textAlign: 'right', fontFamily: mono ? "'DM Mono',monospace" : undefined, ...style }}>{value || '—'}</span>
      </div>
    );
  }

  function Sect({ title }) {
    return (
      <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', padding: '10px 0 4px', marginTop: 4 }}>
        {title}
      </div>
    );
  }

  // ── Type meta ──
  const typeLabels = {
    rental:  '🟢 Active Rental',
    overdue: '⚠️ Overdue',
    booking: isOnline ? '🌐 Online Booking' : '📅 Booking',
    maint:   '🔧 Maintenance',
  };
  const typeColors = {
    rental:  { color: 'var(--gr)' },
    overdue: { color: 'var(--ac)' },
    booking: { color: isOnline ? 'var(--bl)' : 'var(--am)' },
    maint:   { color: 'var(--pu)' },
  };
  const icoColors = {
    rental:  'var(--gr-l)',
    overdue: 'var(--ac-l)',
    booking: isOnline ? 'var(--bl-l)' : 'var(--am-l)',
    maint:   'var(--pu-l)',
  };

  const startLabel = rental.startLabel || rental.start?.split('T')[0] || '—';
  const endLabel   = rental.endLabel   || rental.end?.split('T')[0]   || '—';

  return (
    <div
      className="ovl open"
      style={{ zIndex: 300 }}
      onClick={e => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="modal" style={{ width: 580 }} onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="mhd">
          <div className="m-ico" style={{ background: icoColors[b.type] || 'var(--ac-l)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke={b.type === 'rental' ? 'var(--gr)' : b.type === 'overdue' ? 'var(--ac)' : b.type === 'maint' ? 'var(--pu)' : isOnline ? 'var(--bl)' : 'var(--am)'} strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <path d="M16 2v4M8 2v4M3 10h18"/>
            </svg>
          </div>
          <div>
            <div className="m-tit">{b.label}</div>
            <div className="m-sub">{v?.name || b.vid} · {startLabel} – {endLabel}</div>
          </div>
          <button className="m-cls" onClick={close}>×</button>
        </div>

        {/* ── Body ── */}
        <div className="mbdy" style={{ maxHeight: 520, overflowY: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>

            {/* Booking Info */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Sect title="Booking Info" />
              {!editing && canEdit('rentals') && b.type === 'booking' && (
                <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '3px 10px' }} onClick={beginEdit}>
                  ✏️ Edit
                </button>
              )}
            </div>
            <Row label="Type"     value={typeLabels[b.type] || b.type} style={typeColors[b.type] || {}} />
            <Row label="Vehicle"  value={(v?.icon || '') + ' ' + (v?.name || b.vid)} />

            {editing ? (
              <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 8, padding: '10px 12px', margin: '6px 0' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 600 }}>Pickup Date</label>
                    <input type="date" className="fi" value={editStart} onChange={e => setEditStart(e.target.value)}
                      style={{ fontSize: 11, padding: '4px 6px' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 600 }}>Pickup Time</label>
                    <input type="time" className="fi" value={editT1} onChange={e => setEditT1(e.target.value)}
                      style={{ fontSize: 11, padding: '4px 6px' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 600 }}>Return Date</label>
                    <input type="date" className="fi" value={editEnd} onChange={e => setEditEnd(e.target.value)}
                      style={{ fontSize: 11, padding: '4px 6px' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 600 }}>Return Time</label>
                    <input type="time" className="fi" value={editT2} onChange={e => setEditT2(e.target.value)}
                      style={{ fontSize: 11, padding: '4px 6px' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 8 }}>
                  <button className="btn btn-ghost btn-sm" style={{ fontSize: 10 }} onClick={() => setEditing(false)}>Cancel</button>
                  <button className="btn btn-primary btn-sm" style={{ fontSize: 10 }} onClick={saveEdit}>Save Changes</button>
                </div>
              </div>
            ) : (
              <>
                <Row label="Pickup"   value={`${startLabel}${rental.t1 ? ' · ' + rental.t1 : ''}`} mono />
                <Row label="Return"   value={`${endLabel}${rental.t2 ? ' · ' + rental.t2 : ''}`}   mono
                  style={b.type === 'overdue' ? { color: 'var(--ac)' } : {}} />
              </>
            )}
            <Row label="Duration" value={`${b.span} day${b.span > 1 ? 's' : ''}`} />

            {/* Assignment */}
            {rental && (
              <>
                <Sect title="Assignment" />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--bd)' }}>
                  {rental.assignedTo ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 24, height: 24, borderRadius: '50%', background: users.find(u => u.id === rental.assignedTo)?.color || 'var(--tx3)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700 }}>
                          {users.find(u => u.id === rental.assignedTo)?.initials || '?'}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>
                          {getUserName(rental.assignedTo)}
                        </span>
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: rental.assignPhase === 'checkout' ? 'var(--am-l)' : 'var(--bl-l)', color: rental.assignPhase === 'checkout' ? 'var(--am)' : 'var(--bl)', fontWeight: 600 }}>
                          {rental.assignPhase === 'checkout' ? 'Check-out' : 'Check-in'}
                        </span>
                      </div>
                      {canEdit('rentals') && (
                        <button onClick={() => unassignRental(rental.id)} style={{ fontSize: 10, color: 'var(--ac)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Remove</button>
                      )}
                    </div>
                  ) : (
                    <div style={{ flex: 1 }}>
                      {canEdit('rentals') ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 11, color: 'var(--am)', fontWeight: 600 }}>⚠️ Unassigned</span>
                          <select
                            style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--bd)', background: 'var(--sur)', flex: 1 }}
                            value=""
                            onChange={e => {
                              if (e.target.value) {
                                const phase = (rental.status === 'active' || rental.status === 'overdue') ? 'checkout' : 'checkin';
                                assignRental(rental.id, e.target.value, phase);
                                showToast(`✅ Assigned to ${getUserName(e.target.value)}`);
                              }
                            }}
                          >
                            <option value="">Assign to...</option>
                            {users.filter(u => {
                              if (u.status !== 'active') return false;
                              const role = roles.find(r => r.id === u.roleId);
                              return role?.locked || role?.perms?.rentals?.view === true;
                            }).map(u => (
                              <option key={u.id} value={u.id}>{u.fname} {u.lname} — {(roles.find(r => r.id === u.roleId) || {}).name}</option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <span style={{ fontSize: 11, color: 'var(--am)', fontWeight: 600 }}>⚠️ Unassigned</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Audit trail */}
                {(rental.checkedInBy || rental.checkedOutBy) && (
                  <div style={{ margin: '6px 0', padding: '6px 10px', background: 'var(--sur2)', borderRadius: 6, border: '1px solid var(--bd)' }}>
                    {rental.checkedInBy && (
                      <div style={{ fontSize: 11, color: 'var(--tx2)', display: 'flex', justifyContent: 'space-between' }}>
                        <span>✅ Checked in by <strong>{getUserName(rental.checkedInBy)}</strong></span>
                        <span style={{ color: 'var(--tx3)' }}>{rental.checkedInAt || ''}</span>
                      </div>
                    )}
                    {rental.checkedOutBy && (
                      <div style={{ fontSize: 11, color: 'var(--tx2)', display: 'flex', justifyContent: 'space-between', marginTop: rental.checkedInBy ? 3 : 0 }}>
                        <span>🏁 Checked out by <strong>{getUserName(rental.checkedOutBy)}</strong></span>
                        <span style={{ color: 'var(--tx3)' }}>{rental.checkedOutAt || ''}</span>
                      </div>
                    )}
                    {rental.cashCollectedBy && (
                      <div style={{ fontSize: 11, color: 'var(--tx2)', display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
                        <span>💵 Cash collected by <strong>{getUserName(rental.cashCollectedBy)}</strong></span>
                        <span style={{ color: 'var(--gr)', fontWeight: 600 }}>€{(cashPaidFin > 0 ? cashPaidFin : parseFloat(rental.cashAmount) || 0).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {rental && (
              <>
                {/* Customer */}
                <Sect title="Customer" />
                <Row label="Name"    value={rental.customer} style={{ fontWeight: 700 }} />
                {rental.phone    && <Row label="Phone"      value={rental.phone} />}
                {rental.email    && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: 12, padding: '5px 0', borderBottom: '1px solid var(--bd)' }}>
                    <span style={{ color: 'var(--tx3)', flexShrink: 0, marginRight: 10 }}>Email</span>
                    <a href={`mailto:${rental.email}`} style={{ fontWeight: 500, color: 'var(--bl)' }}>{rental.email}</a>
                  </div>
                )}
                {rental.passport    && <Row label="Passport / ID"    value={rental.passport}    mono />}
                {rental.licence     && <Row label="Driving Licence"  value={rental.licence}     mono />}
                {rental.licExpiry   && <Row label="Licence Expiry"   value={rental.licExpiry}   mono />}
                {rental.nationality && <Row label="Nationality"      value={rental.nationality} />}
                {rental.dob         && <Row label="Date of Birth"    value={rental.dob}         mono />}

                {/* Uploaded Document Photos */}
                {(rental.passportPhoto || rental.licencePhoto || rental.idFile || rental.licenceFile) && (
                  <>
                    <Sect title="📎 Uploaded Documents" />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, margin: '6px 0' }}>
                      {(rental.passportPhoto || rental.idFile) && (
                        <div>
                          <div style={{ fontSize: 10, color: 'var(--tx3)', marginBottom: 3 }}>Passport / ID</div>
                          <img
                            src={rental.passportPhoto || (rental.idFile instanceof File ? URL.createObjectURL(rental.idFile) : rental.idFile)}
                            alt="Passport/ID"
                            style={{ width: '100%', borderRadius: 6, border: '1px solid var(--bd)', maxHeight: 90, objectFit: 'cover' }}
                          />
                        </div>
                      )}
                      {(rental.licencePhoto || rental.licenceFile) && (
                        <div>
                          <div style={{ fontSize: 10, color: 'var(--tx3)', marginBottom: 3 }}>Driving Licence</div>
                          <img
                            src={rental.licencePhoto || (rental.licenceFile instanceof File ? URL.createObjectURL(rental.licenceFile) : rental.licenceFile)}
                            alt="Driving Licence"
                            style={{ width: '100%', borderRadius: 6, border: '1px solid var(--bd)', maxHeight: 90, objectFit: 'cover' }}
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Check-in Vehicle Photos */}
                {rental.checkInPhotos?.length > 0 && (
                  <>
                    <Sect title="📷 Check-in Photos" />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, margin: '6px 0' }}>
                      {rental.checkInPhotos.map((src, i) => (
                        <img
                          key={i}
                          src={src}
                          alt={`Check-in ${i + 1}`}
                          style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--bd)', cursor: 'pointer' }}
                          onClick={() => window.open(src, '_blank')}
                        />
                      ))}
                    </div>
                  </>
                )}

                {/* Signature */}
                <Sect title="Agreement & Signature" />
                {rental.presigned && rental.presignData ? (
                  <>
                    <div style={{ background: 'var(--gr-l)', border: '1px solid var(--gr-b)', borderRadius: 6, padding: '6px 10px', marginBottom: 6, fontSize: 11, color: 'var(--gr)', fontWeight: 600 }}>
                      ✅ Pre-signed online — {rental.presignedAt || ''}
                    </div>
                    <img
                      src={rental.presignData}
                      alt="Signature"
                      style={{ width: '100%', background: '#fff', border: '1px solid var(--bd)', borderRadius: 6, maxHeight: 70, objectFit: 'contain' }}
                    />
                  </>
                ) : rental.signed ? (
                  <div style={{ fontSize: 11, color: 'var(--gr)' }}>✓ Signed at check-in — {rental.checkInTime || ''}</div>
                ) : (
                  <div style={{ fontSize: 11, color: 'var(--tx3)' }}>Not signed yet — customer signs at check-in</div>
                )}
                {/* View / Print contract — always available, prints with a "Not yet signed" stamp if no signature */}
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => openContract(rental, false)}>📋 View contract</button>
                  <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => openContract(rental, true)}>
                    🖨️ Print / PDF{!(rental.signed || rental.presigned) && <span style={{ color: 'var(--am)' }}> · unsigned</span>}
                  </button>
                </div>

                {/* Locations */}
                {rental.pickupLocName && (
                  <>
                    <Sect title="📍 Locations" />
                    <Row label="Pickup" value={'📍 ' + rental.pickupLocName} />
                    {rental.returnLocName && rental.returnLocName !== rental.pickupLocName && (
                      <Row label="Return" value={'📍 ' + rental.returnLocName} />
                    )}
                  </>
                )}

                {/* Insurance & Extras */}
                {(rental.insurance || rental.extras?.length) && (
                  <>
                    <Sect title="Insurance & Extras" />
                    {rental.insurance && <Row label="Insurance" value={'🛡️ ' + rental.insurance} style={{ color: 'var(--bl)' }} />}
                    {rental.extras?.length > 0 && <Row label="Extras" value={rental.extras.join(', ')} style={{ color: 'var(--am)' }} />}
                  </>
                )}

                {/* Financial */}
                <Sect title="Financial" />
                <Row label="Contract"     value={rental.id}          mono />
                <Row label="Base rental"  value={`€${rental.rate || 0}/day × ${rental.calSpan || b.span} days`} />

                {/* Payments breakdown FIRST — one line per PAID pro forma (online card + desk cash),
                   mirrors the list view. Falls back to an expected-prepaid estimate
                   for online bookings with no recorded payment yet. */}
                {paidFin.length > 0 && (
                  <div style={{ background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 6, padding: '8px 10px', margin: '6px 0', fontSize: 11 }}>
                    {paidFin.map(inv => (
                      <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                        <span style={{ color: 'var(--tx3)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.methodLabel || PAY_LABELS[inv.method] || inv.method} · {invoiceDisplayNumber(inv)}</span>
                        <span style={{ color: 'var(--gr)', fontWeight: 600 }}>€{(parseFloat(inv.total) || 0).toFixed(2)}</span>
                        <button type="button" title="View pro forma"
                          onClick={() => { close(); setTimeout(() => openModal('view-invoice', inv.id), 60); }}
                          style={{ border: '1px solid var(--bd)', background: 'var(--sur)', borderRadius: 5, cursor: 'pointer', fontSize: 10, padding: '1px 5px', lineHeight: 1.5 }}>📄</button>
                        <button type="button" title="Print pro forma"
                          onClick={() => printInvoice(inv, companySettings, rentals, vehicles)}
                          style={{ border: '1px solid var(--bd)', background: 'var(--sur)', borderRadius: 5, cursor: 'pointer', fontSize: 10, padding: '1px 5px', lineHeight: 1.5 }}>🖨️</button>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, paddingTop: 3, borderTop: '1px solid var(--bl-b)' }}>
                      <span style={{ color: 'var(--tx3)' }}>{dueFin > 0 ? 'Still due at desk' : 'Fully paid'}</span>
                      <span style={{ color: dueFin > 0 ? 'var(--am)' : 'var(--gr)', fontWeight: 600 }}>{dueFin > 0 ? `€${dueFin.toFixed(2)}` : '✓'}</span>
                    </div>
                  </div>
                )}
                {paidFin.length === 0 && ['stripe','online','viva','paypal'].includes(rental.payMethod) && (() => {
                  const diffOwed = parseFloat(rental.differenceOwed) || 0;
                  const total = parseFloat(rental.agreedPrice) || 0;
                  const paidEst = diffOwed > 0 ? total - diffOwed : total;
                  return (
                    <div style={{ background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 6, padding: '8px 10px', margin: '6px 0', fontSize: 11 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--tx3)' }}>Pre-paid online</span>
                        <span style={{ color: 'var(--gr)', fontWeight: 600 }}>€{paidEst.toFixed(2)}</span>
                      </div>
                      {diffOwed > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
                          <span style={{ color: 'var(--tx3)' }}>Due at desk</span>
                          <span style={{ color: 'var(--am)', fontWeight: 600 }}>€{diffOwed.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Agreed total summary — sits BELOW the breakdown as the bottom line */}
                <Row label="Agreed Total" value={`€${rental.agreedPrice}`}
                  style={{ color: 'var(--gr)', fontSize: 14, fontWeight: 700 }} />
                {/* Single payment-method row only when there's NO per-method breakdown above
                    (otherwise it reads as if the whole total used one method) */}
                {paidFin.length === 0 && (
                  <Row label="Payment"      value={PAY_LABELS[rental.payMethod] || rental.payMethod || '—'} />
                )}
                {rental.deposit > 0 && (
                  <Row label="Deposit" value={`€${rental.deposit}${rental.depositMethod ? ' · ' + rental.depositMethod : ''}`} mono />
                )}
                {rental.payMethod === 'bank' && (
                  rental.bankTransferVerified
                    ? <div style={{ background: 'var(--gr-l)', border: '1px solid var(--gr-b)', borderRadius: 6, padding: '8px 10px', margin: '6px 0', fontSize: 11, color: 'var(--gr)', fontWeight: 600 }}>
                        ✅ Bank transfer payment confirmed — pro forma marked as paid
                      </div>
                    : <div style={{ background: 'var(--am-l)', border: '1px solid var(--am-b)', borderRadius: 6, padding: '8px 10px', margin: '6px 0', fontSize: 11, color: 'var(--am)' }}>
                        🏦 Bank transfer — verify payment received before starting rental
                      </div>
                )}

                {rental.bookingRef && (
                  <Row label="Booking Ref" value={rental.bookingRef} mono style={{ color: 'var(--bl)' }} />
                )}
                {rental.notes && !rental.notes.toLowerCase().includes('online booking') && (
                  <div style={{ fontSize: 11, color: 'var(--tx2)', background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 6, padding: '8px 10px', marginTop: 6 }}>
                    💬 {rental.notes}
                  </div>
                )}
              </>
            )}

          </div>
        </div>

        {/* ── Footer ── */}
        <div className="mft">
          {/* Pro Forma(s) — opens the single doc or the multi-doc list */}
          {(() => {
            const linked = (invoices || []).filter(i => i.contract === rental.id || i.contractId === rental.id);
            if (linked.length === 0) return null;
            return (
              <button className="btn btn-ghost" style={{ marginRight: 'auto' }}
                onClick={() => {
                  const multi = linked.length > 1 || rental.type === 'lease';
                  close();
                  setTimeout(() => openModal(multi ? 'lease-invoices' : 'view-invoice', multi ? rental.id : linked[0].id), 60);
                }}>
                🧾 {linked.length > 1 ? `Pro Formas (${linked.length})` : 'Pro Forma'}
              </button>
            );
          })()}
          {(b.type === 'rental' || b.type === 'overdue') && (
            <>
              <button className="btn btn-ghost" onClick={close}>Close</button>
              {rental.type !== 'lease' && (
                <button className="btn btn-ghost" onClick={() => { close(); setTimeout(() => openModal('extend-rental', b.vid), 60); }}>
                  ⏱ Extend
                </button>
              )}
              <button className="btn btn-primary" onClick={() => { openModal(rental.type === 'lease' ? 'end-lease' : 'end-rental', b.vid); close(); }}>
                {rental.type === 'lease' ? 'End Lease' : 'End Rental'}
              </button>
            </>
          )}
          {b.type === 'booking' && (
            <>
              <button className="btn btn-ghost" onClick={close}>Close</button>
              <button className="btn btn-ghost" style={{ color: 'var(--ac)', borderColor: 'var(--ac-b)' }} onClick={cancelBooking}>
                ✕ Cancel
              </button>
              <button className="btn btn-primary" style={{ background: 'var(--gr)', borderColor: 'var(--gr)' }} onClick={startRental}>
                ✅ Start Rental — Check-in
              </button>
            </>
          )}
          {b.type !== 'rental' && b.type !== 'overdue' && b.type !== 'booking' && (
            <button className="btn btn-ghost" onClick={close}>Close</button>
          )}
        </div>

      </div>
    </div>
  );
}
